import { FileType } from "../types/enums";
import { File as FileInterface } from "../types/interfaces";
import imageCompression from "browser-image-compression";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import JSZip from "jszip";

export const handleImageUpload = async (file: File) => {
  const options = {
    maxSizeMB: 0.5,
    maxWidthOrHeight: 1024,
    useWebWorker: true,
  };

  try {
    const compressedFile = await imageCompression(file, options);
    return compressedFile;
  } catch (error) {
    console.error("Image compression error:", error);
  }
};

export const getNotImagesFiles = (allFiles: FileInterface[]): FileInterface[] =>
  allFiles.filter(file => {
    const fileNameSplit = file?.key?.toLocaleLowerCase().split(".");
    return !["jpg", "jpeg", "png", "dcm", "stl"].includes(
      fileNameSplit[fileNameSplit.length - 1]
    );
  });
export const getImagesFromFiles = (
  allFiles: FileInterface[]
): FileInterface[] =>
  allFiles.filter(file => {
    const fileNameSplit = file?.key?.toLocaleLowerCase().split(".");
    return ["jpg", "jpeg", "png", "dcm", "stl"].includes(
      fileNameSplit[fileNameSplit.length - 1]
    );
  });

export const get3OXZFile = (allFiles: FileInterface[]): FileInterface[] => {
  return allFiles.filter(file =>
    file?.key?.toLowerCase().toLowerCase().includes("3oxz")
  );
};

export const getTraceabilitiesFile = (
  allFiles: FileInterface[]
): FileInterface[] =>
  allFiles.filter(
    file =>
      (file?.fileType ?? file?.name)?.toUpperCase() ===
        FileType.BILLING_DENTIST_TRACEABILITY ||
      (file?.fileType ?? file?.name)?.toUpperCase() ===
        FileType.BILLING_PATIENT_TRACEABILITY
  );

export const getImagesFile = (allFiles: FileInterface[]): FileInterface[] =>
  allFiles.filter(file => {
    const fileNameSplit = file?.key?.toLocaleLowerCase().split(".");
    return (
      ["jpg", "jpeg", "png", "dcm", "stl", "html"].includes(
        fileNameSplit[fileNameSplit.length - 1]
      ) ||
      file.fileType === FileType.CLIENT_FILE ||
      file.fileType === FileType.LABS_FILE ||
      file.fileType === FileType.ORDER_ADDON_FILE ||
      file.fileType === FileType.PICKUP
    );
  });

export const isUUID = (id: string) => {
  const regex =
    /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}/;
  return id.match(regex);
};

// Files extracted from an archive by the archive-extractor Lambda are stored
// under extracted/<archive file id>/, followed by their path in the archive.
const EXTRACTED_PREFIX = /^extracted\/[^/]+\//;

// Uploaded file keys start with a UUID, followed by the original file name.
export const getFileDisplayName = (key: string): string => {
  const name = key.replace(EXTRACTED_PREFIX, "");
  return isUUID(name) ? name.substring(37) : name;
};

/** Whether the file was extracted from an archive by the Lambda. */
export const isExtractedFile = (key: string): boolean =>
  EXTRACTED_PREFIX.test(key);

/** Whether the Lambda already extracted files of this archive into `files`. */
export const hasExtractedFiles = (
  archiveId: string,
  files: FileInterface[]
): boolean =>
  files.some(file => file.key?.startsWith(`extracted/${archiveId}/`));

export const getValideFiles = (files: FileInterface[]) => {
  return files
    .filter(file =>
      [
        FileType.LABS_FILE.toLowerCase().replace("_", ""),
        FileType.CLIENT_FILE.toLowerCase().replace("_", ""),
        FileType.ORDER_FILE.toLowerCase().replace("_", ""),
        FileType.BILLING_DENTIST_TRACEABILITY.toLowerCase().replace("_", ""),
        FileType.BILLING_PATIENT_TRACEABILITY.toLowerCase().replace("_", ""),
        FileType.ORDER_ADDON_FILE.toLowerCase().replace("_", ""),
        FileType.PICKUP.toLowerCase(),
      ].includes(file.fileType.toLowerCase().replace("_", ""))
    )
    .map(({ __typename, ...rest }) => rest);
};

export const createPDFFromArrayText = async ({
  data,
  dimensions = { height: 595, width: 842, size: 18, x: 50, y: 14 },
}: {
  data: string[];
  dimensions?: {
    height: number;
    width: number;
    size: number;
    x: number;
    y: number;
  };
}) => {
  const pdfDoc = await PDFDocument.create();
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const page = pdfDoc.addPage([dimensions.width, dimensions.height]);

  const textHeight = dimensions.size + (data.length - 1) * dimensions.y;

  const startY = (dimensions.height + textHeight) / 2 - dimensions.size;

  data.forEach((line, index) => {
    page.drawText(line, {
      x: dimensions.x,
      y: startY - index * dimensions.y,
      size: dimensions.size,
      font,
    });
  });

  const pdfBytes = await pdfDoc.save();
  return new Blob([pdfBytes], {
    type: "application/pdf",
  });
};

export const createPDF = async <T extends Record<string, unknown>>({
  columns,
  data,
  title,
}: {
  columns: { label: string; key: keyof T & string }[];
  data: T[];
  title: string;
}): Promise<Blob> => {
  const pdfDoc = await PDFDocument.create();
  let page = pdfDoc.addPage([595, 842]);
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const pageHeight = page.getHeight();
  const pageWidth = page.getWidth();
  const tableX = 40;
  const tableWidth = page.getWidth() - tableX * 2;
  const columnWidth = tableWidth / columns.length;
  const tableRight = tableX + tableWidth;
  const lineInset = 1;
  const rowHeight = 25;
  let y = 0;
  const bottomMargin = 40;

  const drawPageHeader = () => {
    page.drawText(title, {
      x: 40,
      y: pageHeight - 40,
      size: 18,
      font: boldFont,
    });
    y = pageHeight - 80;
    columns.forEach((column, index) => {
      page.drawText(column.label, {
        x: tableX + index * columnWidth + 5,
        y: y + 3,
        size: 11,
        font: boldFont,
      });
    });
    y -= rowHeight;
  };

  drawPageHeader();

  data.forEach(item => {
    if (y - rowHeight < bottomMargin) {
      page = pdfDoc.addPage([pageWidth, pageHeight]);
      drawPageHeader();
    }
    page.drawLine({
      start: {
        x: tableX + lineInset,
        y: y - 5,
      },
      end: {
        x: tableRight - lineInset,
        y: y - 5,
      },
      thickness: 0.5,
      color: rgb(0.7, 0.7, 0.7),
    });

    columns.forEach((column, index) => {
      const value = item[column.key];
      page.drawText(
        value === null || value === undefined ? "" : String(value),
        {
          x: tableX + index * columnWidth + 5,
          y: y + 3,
          size: 10,
          font,
        }
      );
    });

    y -= rowHeight;
  });

  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes], {
    type: "application/pdf",
  });

  return blob;
};

export const downloadFile = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

// Waits before the 2nd and the 3rd attempt: three tries in all, so a supplier
// on a slow link sees an error within a few minutes rather than never.
const DOWNLOAD_RETRY_DELAYS_MS = [2000, 5000];

class HttpStatusError extends Error {
  constructor(readonly status: number) {
    super(`HTTP ${status}`);
  }
}

// A 4xx (expired signed URL, missing key) fails the same way every time;
// timeouts, throttling and server errors may pass on the next try.
const isRetryableStatus = (status: number): boolean =>
  status === 408 || status === 429 || status >= 500;

/**
 * Downloads a file as a Blob, retrying network errors and retryable statuses.
 * The body is read inside the retried block: fetch() resolves on the headers,
 * so a download cut halfway only fails while reading the body.
 */
export const fetchBlobWithRetry = async (
  url: string,
  retryDelaysMs: number[] = DOWNLOAD_RETRY_DELAYS_MS
): Promise<Blob> => {
  for (let attempt = 0; ; attempt++) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new HttpStatusError(response.status);
      }
      return await response.blob();
    } catch (error) {
      const retryable =
        !(error instanceof HttpStatusError) || isRetryableStatus(error.status);
      if (!retryable || attempt >= retryDelaysMs.length) {
        throw error;
      }
      await new Promise(resolve => setTimeout(resolve, retryDelaysMs[attempt]));
    }
  }
};

/** Files to archive, keyed by the folder they go into inside the zip. */
export type OrderFilesArchiveGroups = Record<string, FileInterface[]>;

const archiveEntryBaseName = (file: FileInterface): string =>
  (file.name ?? file.key).split("/").pop() || file.key;

/** "scan.stl" already taken → "scan (2).stl", then "scan (3).stl", ... */
const uniqueArchiveEntryName = (name: string, taken: Set<string>): string => {
  let candidate = name;
  const dotIndex = name.lastIndexOf(".");
  const stem = dotIndex > 0 ? name.slice(0, dotIndex) : name;
  const extension = dotIndex > 0 ? name.slice(dotIndex) : "";
  for (let n = 2; taken.has(candidate); n++) {
    candidate = `${stem} (${n})${extension}`;
  }
  taken.add(candidate);
  return candidate;
};

const fetchArchiveEntryContent = async (
  file: FileInterface,
  retryDelaysMs?: number[]
): Promise<Blob> => {
  try {
    return await fetchBlobWithRetry(file.url, retryDelaysMs);
  } catch (error) {
    if (error instanceof HttpStatusError) {
      throw new Error(
        `Could not download ${archiveEntryBaseName(file)} (HTTP ${error.status})`,
        { cause: error }
      );
    }
    throw error;
  }
};

/**
 * Zips every file of every group under a folder named after the group.
 * All-or-nothing: every file is fetched, with retries, before anything is
 * written to the archive, so a failed download rejects and nothing partial
 * exists. `retryDelaysMs` defaults to the one of fetchBlobWithRetry.
 */
export const buildOrderFilesArchive = async (
  groups: OrderFilesArchiveGroups,
  retryDelaysMs?: number[]
): Promise<Blob> => {
  const entries: { path: string; file: FileInterface }[] = [];
  for (const [folder, files] of Object.entries(groups)) {
    const taken = new Set<string>();
    for (const file of files) {
      const entryName = uniqueArchiveEntryName(
        archiveEntryBaseName(file),
        taken
      );
      entries.push({ path: `${folder}/${entryName}`, file });
    }
  }

  const contents = await Promise.all(
    entries.map(entry => fetchArchiveEntryContent(entry.file, retryDelaysMs))
  );

  const zip = new JSZip();
  entries.forEach((entry, index) => zip.file(entry.path, contents[index]));
  return zip.generateAsync({ type: "blob" });
};
